import { useEffect, useState, useCallback } from "react";
import { Row, Col, Typography, Badge, Spin, Input, Empty } from "antd";
import { notify } from "@/shared/utils/notify";
import { SearchOutlined } from "@ant-design/icons";
import { IOrder } from "../../../redux/@types";
import { ordersService, orderStatusService } from "../../../shared/services";
import { OrderCard } from "./OrderCard";
import { useTableOrders } from "../hooks/useTableOrders";

const { Title, Text } = Typography;

interface DataStatus {
  id: number;
  name: string;
  description: string;
  status_code: string;
  color: string;
  deletedAt: null;
  createdAt: Date;
  updatedAt: Date;
}

interface KanbanColumn {
  id: number;
  title: string;
  statusIds: number[];
  color: string;
  backgroundColor: string;
}

export const OrdersKanban = ({ storeId }: { storeId?: number }) => {
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [orderStatus, setOrderStatus] = useState<DataStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const { changeOrderForUpdate } = useTableOrders();

  // Definir las columnas del Kanban
  const columns: KanbanColumn[] = [
    {
      id: 1,
      title: "PARA ACEPTAR",
      statusIds: [1], // Pendiente por pago
      color: "#52c41a",
      backgroundColor: "#d4f4dd",
    },
    {
      id: 2,
      title: "EN PREPARACIÓN",
      statusIds: [2], // Preparando envío / En proceso
      color: "#faad14",
      backgroundColor: "#fff7e6",
    },
    {
      id: 3,
      title: "PARA ENTREGAR",
      statusIds: [3, 4], // Pendiente por envío / En camino
      color: "#1890ff",
      backgroundColor: "#e6f7ff",
    },
  ];

  const getOrderStatus = async () => {
    try {
      const res = await orderStatusService.find({ query: { $limit: 9999999999999 } });
      setOrderStatus(res.data);
    } catch (err: any) {
      notify.error("Error al cargar estados de órdenes");
    }
  };

  const getOrders = useCallback(
    async (showLoading = true) => {
      if (showLoading) setLoading(true);
      try {
        const query: Record<string, unknown> = {
          $limit: 9999999999999,
          $sort: { id: -1 },
          order_status_id: {
            $in: [1, 2, 3, 4],
          },
        };
        if (storeId != null) {
          query.store_id = storeId;
        }
        const res = await ordersService.find({ query });
        setOrders(res.data);
      } catch (err: any) {
        notify.error("Error al cargar órdenes");
      } finally {
        setLoading(false);
      }
    },
    [storeId]
  );

  useEffect(() => {
    getOrderStatus();
  }, []);

  useEffect(() => {
    getOrders();
    const interval = setInterval(() => {
      getOrders(false);
    }, 30000);
    return () => clearInterval(interval);
  }, [getOrders]);

  const getOrdersByColumn = (column: KanbanColumn) => {
    return orders.filter((order) => {
      const matchesStatus = column.statusIds.includes(order.order_status_id);
      const matchesSearch = searchText
        ? order.id.toString().includes(searchText) ||
          `${order.user?.first_name} ${order.user?.last_name}`
            .toLowerCase()
            .includes(searchText.toLowerCase())
        : true;
      return matchesStatus && matchesSearch;
    });
  };

  const handleViewDetails = (order: IOrder) => {
    changeOrderForUpdate(order);
  };

  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "60vh" 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: "0 16px" }}>
      {/* Header con búsqueda */}
      <div style={{ 
        marginBottom: 24, 
        display: "flex", 
        justifyContent: "space-between",
        alignItems: "center" 
      }}>
        <Title level={3} style={{ margin: 0 }}>
          Órdenes
        </Title>
        <Input
          placeholder="Ingresar ID de orden"
          prefix={<SearchOutlined />}
          style={{ width: 300 }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>

      {/* Columnas Kanban */}
      <Row gutter={16}>
        {columns.map((column) => {
          const columnOrders = getOrdersByColumn(column);
          
          return (
            <Col key={column.id} xs={24} sm={24} md={8} lg={8} xl={8}>
              <div
                style={{
                  backgroundColor: column.backgroundColor,
                  borderRadius: 12,
                  padding: 16,
                  minHeight: "calc(100vh - 200px)",
                  border: "1px solid #e8e8e8",
                }}
              >
                {/* Header de columna */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 16,
                    paddingBottom: 12,
                    borderBottom: `3px solid ${column.color}`,
                  }}
                >
                  <Text
                    strong
                    style={{
                      fontSize: 14,
                      color: "#262626",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {column.title}
                  </Text>
                  <Badge
                    count={columnOrders.length}
                    style={{
                      backgroundColor: column.color,
                    }}
                  />
                </div>

                {/* Lista de órdenes */}
                <div
                  style={{
                    maxHeight: "calc(100vh - 280px)",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {columnOrders.length === 0 ? (
                    <Empty
                      description="No hay órdenes"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      style={{ marginTop: 40 }}
                    />
                  ) : (
                columnOrders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onViewDetails={handleViewDetails}
                    showTimer={column.id === 2} // Mostrar timer solo en "En Preparación"
                    onStatusChanged={getOrders} // Refrescar órdenes cuando cambia el estado
                    orderStatus={orderStatus} // Pasar los estados para buscar el ID correcto de cancelado
                  />
                ))
                  )}
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

